/**
 * Reads the `data: …` lines of a server-sent-events body, as the Alma
 * endpoints send them (one JSON payload per event, `[DONE]` at the end).
 *
 * A network chunk can end in the middle of an event, so the unfinished tail is
 * held back and joined to the next chunk. Splitting every chunk on its own
 * drops that event, which loses a few words of Alma's reply.
 */
export function createSseParser() {
  let buffer = '';

  /** Payloads of every complete `data:` line in this chunk. */
  function push(chunk: string): string[] {
    buffer += chunk;
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    return lines.flatMap(payloadOf);
  }

  /** Whatever is left once the stream has ended (an event with no trailing newline). */
  function flush(): string[] {
    const rest = buffer;
    buffer = '';
    return payloadOf(rest);
  }

  return { push, flush };
}

function payloadOf(line: string): string[] {
  const trimmed = line.endsWith('\r') ? line.slice(0, -1) : line;
  return trimmed.startsWith('data: ') ? [trimmed.slice(6)] : [];
}

/** Yields each payload of a response body as it arrives, then any final one. */
export async function* readSse(body: ReadableStream<Uint8Array>): AsyncGenerator<string> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = createSseParser();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      yield* parser.push(decoder.decode(value, { stream: true }));
    }
    yield* parser.push(decoder.decode());
    yield* parser.flush();
  } finally {
    reader.releaseLock();
  }
}
